/**
 * @file NewMemberPage.js
 * @module src/js/pages/memberZone/NewMemberPage
 * @description
 * **Page component for adding a new member in the EDALab frontend.**
 * - Provides a form to input new member details including ID, institute, level, password, name, and photo.
 * - Utilizes `AddMemberDataContent` component for structured data input.
 * - Handles form submission to add the member via the `addMember` function.
 * - Displays success or error messages based on the operation result.
 * - Redirects to login if the user is not authenticated.
 *
 * @version
 * <p>
 *  2025-11-25: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-01: Add 'choose' and -1 as default values for institute and level. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-01: Add focus to Member ID input on mount and after submission. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Move messages to the modal system. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to useInitTokenTest and useCheckAuthAndFetch hooks. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import useCheckAuthAndFetch from "../../hooks/useCheckAuthAndFetch";
import useInitTokenTest from "../../hooks/useInitTokenTest";
import ThemeToggledWrapper from "../../components/ThemeToggledWrapper";
import AddMemberDataContent from "../../components/AddMemberDataContent";

export default function NewMemberPage() {
    const { pathname } = useLocation();
    const initTokenTest = useInitTokenTest();
    const checkAuthAndFetch = useCheckAuthAndFetch();
    const headerRef = useRef(null);
    const memberIDRef = useRef(null);

    useEffect(() => {
        if (memberIDRef.current) {
            memberIDRef.current.focus();
        }
    }, []);

    useEffect(() => {
        checkAuthAndFetch({ fetchData: false }); // Check uid === "Guest"
        initTokenTest({ from: pathname }); // Check uid !== "Guest" but token expires
    }, [checkAuthAndFetch, initTokenTest, pathname]);

    return (
        <ThemeToggledWrapper id="new-member" lightStyle={2} darkStyle={4}>
            <div className="inner">
                <header
                    className="major special"
                    ref={headerRef}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                >
                    <h2>New Member</h2>
                    <span style={{ textAlign: 'center', fontSize: '0.9em', marginTop: '25px' }}>
                        (Synchronized across&nbsp;
                            <Link to='/members'>Members</Link>,&nbsp;
                            <Link to='/member-zone/contact'>Contact</Link>, and&nbsp;
                            <Link to='/member-zone/profile'>Profile</Link> pages)
                    </span>
                </header>
                <div id="wrapper" className="divided">
                    <section className="spotlight style1 orient-right content-align-left image-position-center onscroll-image-fade-in" id="first">
                        <AddMemberDataContent
                            headerRef={headerRef}
                            memberIDRef={memberIDRef}
                        />
                    </section>
                </div>
            </div>
        </ThemeToggledWrapper>
    )
}
